/**
 * Composant DashboardTab pour la page Satisfaction
 * Affiche les graphiques du tableau de bord
 */

import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Briefcase, BarChart3, Star, Users, FileText } from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { renderStars } from './GlobalRating';

export const DashboardTab = ({ stats, types }) => {
  // Préparer données pour graphiques
  const insertionData = stats?.insertion ? [
    { name: 'En emploi', value: stats.insertion.by_statut?.en_emploi || 0, color: '#22C55E' },
    { name: 'En recherche', value: stats.insertion.by_statut?.en_recherche || 0, color: '#3B82F6' },
    { name: 'Autre emploi', value: stats.insertion.by_statut?.autre_emploi_bpjeps || 0, color: '#8B5CF6' },
    { name: 'Non lié', value: stats.insertion.by_statut?.emploi_non_lie || 0, color: '#F59E0B' },
    { name: 'Arrêt', value: stats.insertion.by_statut?.arret || 0, color: '#EF4444' },
  ].filter(d => d.value > 0) : [];

  const satisfactionByType = stats?.questionnaires?.par_type ? Object.entries(stats.questionnaires.par_type).map(([type, data]) => ({
    name: types[type]?.label || type,
    total: data.total,
    completes: data.completes
  })) : [];

  return (
    <div className="space-y-6" data-testid="dashboard-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Graphique insertion */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5 text-[#8DC63F]" />
              Insertion professionnelle
            </CardTitle>
          </CardHeader>
          <CardContent>
            {insertionData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={insertionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  >
                    {insertionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Aucun moniteur diplômé</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Graphique questionnaires */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#8DC63F]" />
              Questionnaires par type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {satisfactionByType.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={satisfactionByType}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="total" name="Envoyés" fill="#3B82F6" />
                  <Bar dataKey="completes" name="Complétés" fill="#22C55E" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Aucun questionnaire envoyé</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Moyennes de satisfaction */}
      {stats?.moyennes && Object.keys(stats.moyennes).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" />
              Notes moyennes par catégorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {Object.entries(stats.moyennes).map(([type, data]) => (
                <div key={type} className="p-4 bg-slate-50 rounded-lg">
                  <p className="font-medium text-slate-700 mb-2">{types[type]?.label || type}</p>
                  {renderStars(data.moyenne_globale)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardTab;
